import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, FolderKanban, CheckCircle2, Clock, FileText, Upload, TrendingUp, CalendarClock } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";

function Kpi({ icon: Icon, label, value, hint, accent, testId }) {
  return (
    <Card className="card-lift" data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-medium">{label}</p>
            <p className="mt-2 font-heading text-3xl font-bold tabular-nums">{value ?? "—"}</p>
            {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
          </div>
          <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${accent}18`, color: accent }}>
            <Icon size={18} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/dashboard/stats");
        setStats(r.data);
      } catch (_e) { void _e; } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <header>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Visão geral</p>
        <h1 className="font-heading text-3xl font-bold mt-1">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">Indicadores em tempo real dos processos e documentos.</p>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-[110px] rounded-xl" />)
        ) : (
          <>
            <Kpi icon={Users} label="Clientes" value={stats?.total_clients} accent="#114B5F" testId="kpi-clients" />
            <Kpi icon={FolderKanban} label="Projetos ativos" value={stats?.active_projects} accent="#0EA5E9" testId="kpi-active-projects" />
            <Kpi icon={CheckCircle2} label="Finalizados" value={stats?.completed_projects} accent="#10B981" testId="kpi-completed-projects" />
            <Kpi icon={Clock} label="Aguardando docs" value={stats?.waiting_projects} accent="#F59E0B" testId="kpi-waiting-projects" />
            <Kpi icon={FileText} label="Docs pendentes" value={stats?.pending_docs} accent="#D62828" testId="kpi-pending-docs" />
            <Kpi icon={Upload} label="Enviados hoje" value={stats?.docs_today} accent="#8B5CF6" testId="kpi-docs-today" />
          </>
        )}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="dash-recent-activity">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={16} className="text-primary" />
              <h3 className="font-heading font-semibold">Últimas atividades</h3>
            </div>
            {loading ? (
              <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-9 rounded" />)}</div>
            ) : stats?.recent_history?.length ? (
              <ul className="divide-y">
                {stats.recent_history.map((h) => (
                  <li key={h.id} className="py-3 flex items-start gap-3 text-sm">
                    <span className="w-2 h-2 mt-2 rounded-full bg-primary" />
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground truncate">{h.description}</p>
                      <p className="text-xs text-muted-foreground">{h.user_name} • {formatDateTime(h.created_at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhuma atividade recente.</p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="dash-upcoming-projects">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarClock size={16} className="text-primary" />
              <h3 className="font-heading font-semibold">Próximos prazos</h3>
            </div>
            {loading ? (
              <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded" />)}</div>
            ) : stats?.upcoming_projects?.length ? (
              <ul className="space-y-3">
                {stats.upcoming_projects.map((p) => (
                  <li key={p.id}>
                    <button
                      onClick={() => navigate(`/projects/${p.id}`)}
                      className="w-full text-left p-3 rounded-lg border hover:border-primary/40 hover:bg-muted/40 transition-colors"
                      data-testid={`upcoming-project-${p.id}`}
                    >
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Prazo: {formatDate(p.due_date)}</p>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhum prazo próximo.</p>
            )}
          </CardContent>
        </Card>
      </section>

      <section>
        <Card data-testid="dash-recent-uploads">
          <CardContent className="p-6">
            <h3 className="font-heading font-semibold mb-4">Uploads recentes</h3>
            {loading ? (
              <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 rounded" />)}</div>
            ) : stats?.recent_docs?.length ? (
              <ul className="divide-y">
                {stats.recent_docs.map((d) => (
                  <li key={d.id} className="py-3 flex items-center gap-3 text-sm">
                    <FileText size={16} className="text-muted-foreground" />
                    <button
                      onClick={() => navigate(`/projects/${d.project_id}`)}
                      className="flex-1 text-left truncate hover:text-primary transition-colors"
                      data-testid={`recent-doc-${d.id}`}
                    >
                      {d.name || d.filename}
                    </button>
                    <StatusBadge statusKey={d.status_key} />
                    <span className="text-xs text-muted-foreground w-32 text-right hidden sm:block">{formatDateTime(d.created_at)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhum upload recente.</p>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
